import {
    ButtonPrimary,
    ButtonSecondary,
    ErrorFeedbackScreen,
    InfoFeedbackScreen,
    Stack,
    SuccessFeedbackScreen,
    Text2,
    Text4,
    skinVars,
} from '@telefonica/mistica';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EProblemFeedback, EProblemFeedbackPhase, EProblemFeedbackType } from '../../../models/Problems.interfaces';
import navigateParams from '../../../utils/navigation/navigateParams';
import './index.scss';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';

export const IncidentsFeedback: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { trackingScreenView } = new TrackingEvents();
    const { state } = useLocation();
    const [params] = useSearchParams();
    const contactWhatsapp = process.env.REACT_APP_WEBVIEW_NUMBER_CONTACT_WHATSAPP as string;

    const sendToNative = (url: string, type: string, title: string, _queryParams?: { [key: string]: any }) => {
        const queryParams = { title, ..._queryParams };
        if (navigateParams(url, type, queryParams)) {
            navigate(
                `${url}${_queryParams?.id ? `?id=${_queryParams.id}` : ''}${
                    _queryParams?.action ? `&action=${_queryParams.action}` : ''
                }`,
            );
        }
    };

    const getDescription = (description: any, IncidentNumber: any) => {
        return (
            <Stack space={16}>
                <Text4 regular color={skinVars.colors.textSecondary}>
                    {description}
                </Text4>
                <Text2 regular color={skinVars.colors.textSecondary}>
                    {IncidentNumber}
                </Text2>
            </Stack>
        );
    };

    const setComponentFeedback = () => {
        if (
            state?.stateFeedback === 'SUCCESS' ||
            params.get('type')?.toLocaleLowerCase() === EProblemFeedbackType.SUCCESS
        ) {
            return (
                <SuccessFeedbackScreen
                    title={t('supportSuccessFeedbackTitle')}
                    description={
                        getDescription(
                            t('supportSuccessFeedbackDescription'),
                            `Nº: ${state?.code || params.get('code')}`,
                        ) as any
                    }
                    primaryButton={
                        <ButtonPrimary
                            onPress={() => sendToNative('/support/incidents', 'modeBrowser', t('breadcrumbsSupport'))}
                        >
                            {t('supportSuccessFeedbackButton')}
                        </ButtonPrimary>
                    }
                ></SuccessFeedbackScreen>
            );
        } else if (
            state?.stateFeedback === 'INFO' ||
            params.get('type')?.toLocaleLowerCase() === EProblemFeedbackType.INFO
        ) {
            if (state?.titleFeedback === 'Incidencia pendiente' || params.get('phase') === EProblemFeedbackPhase.CUR) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackPendingIncidentTitle')}
                        description={
                            getDescription(
                                t('supportInfoFeedbackPendingIncidentDescription'),
                                state?.code || params.get('code') ? `Nº: ${state?.code || params.get('code')}` : '',
                            ) as any
                        }
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    window.open(
                                        `https://wa.me/${contactWhatsapp}?text=Quiero%20continuar%20con%20las%20comprobaciones%20para%20resolver%20mi%20incidencia`,
                                        '_blank',
                                    );
                                }}
                            >
                                {t('goThroughWhatsapp')}
                            </ButtonPrimary>
                        }
                        secondaryButton={
                            <ButtonSecondary onPress={() => navigate('/support/incidents')}>
                                {t('back')}
                            </ButtonSecondary>
                        }
                    />
                );
            }
            if (
                state?.titleFeedback === 'Estamos revisando tu incidencia' ||
                params.get('phase') === EProblemFeedbackPhase.REV
            ) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackReviewIncidenTitle')}
                        description={
                            getDescription(
                                t('supportInfoFeedbackReviewIncidentDescription'),
                                `Nº: ${state?.code || params.get('code')}`,
                            ) as any
                        }
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('buttonUnderstoodFeedback'),
                                        {
                                            id: params.get('id'),
                                            action: params.get('action'),
                                        },
                                    );
                                }}
                            >
                                {t('buttonUnderstoodFeedback')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }
            if (params.get('phase') === EProblemFeedbackPhase.EXT) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackExistsIncidentTitle')}
                        description={t('supportInfoFeedbackExistsIncidentDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }
            if (params.get('phase') === EProblemFeedbackPhase.REV_TECH) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackReviewIncidenTitle')}
                        description={`${t('supportInfoFeedbackReviewIncidentDescription')}\n\n Nº: ${params.get(
                            'code',
                        )}`}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }

            if (params.get('phase') === EProblemFeedbackPhase.SHIPMENT_PENDING) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackInProgressIncidentTitle')}
                        description={t('supportInfoFeedbackShipmentPendingIncidentDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }

            if (params.get('phase') === EProblemFeedbackPhase.TECHNICIAN_ASSIGNMENT_PENDING) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackReviewIncidenTitle')}
                        description={`${t(
                            'supportInfoFeedbackReviewTechnicalIncidentDescription',
                        )}\n\n Nº: ${params.get('code')}`}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }

            if (state?.titleFeedback === 'Incidencia anulada' || params.get('phase') === EProblemFeedbackPhase.CER) {
                return (
                    <InfoFeedbackScreen
                        title={t('supportInfoFeedbackCanceledIncidentTitle')}
                        description={
                            getDescription(
                                t('supportInfoFeedbackCanceledIncidentDescription'),
                                `Nº: ${state?.code || params.get('code')}`,
                            ) as any
                        }
                        primaryButton={
                            <ButtonPrimary
                                onPress={() =>
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    )
                                }
                            >
                                {t('supportInfoFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></InfoFeedbackScreen>
                );
            }
        } else if (
            state?.stateFeedback === 'ERROR' ||
            params.get('phase')?.toLocaleLowerCase() === EProblemFeedbackType.ERROR
        ) {
            if (
                state?.titleFeedback === 'Ha ocurrido un error' ||
                params.get('error')?.toLocaleLowerCase() === EProblemFeedback.INCIDENCE
            ) {
                return (
                    <ErrorFeedbackScreen
                        title={t('supportErrorFeedbackErrorOccurredTitle')}
                        description={t('supportErrorFeedbackErrorOccurredDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></ErrorFeedbackScreen>
                );
            }
            if (
                state?.titleFeedback === 'Algo ha fallado' ||
                params.get('error')?.toLocaleLowerCase() === EProblemFeedback.SERVICE
            ) {
                return (
                    <ErrorFeedbackScreen
                        title={t('supportErrorFeedbackFailedTitle')}
                        description={t('supportErrorFeedbackFailedDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></ErrorFeedbackScreen>
                );
            }
            if (
                state?.titleFeedback === 'Algo ha fallado' ||
                params.get('error')?.toLocaleLowerCase() === EProblemFeedback.DIAGNOSIS
            ) {
                return (
                    <ErrorFeedbackScreen
                        title={t('supportErrorFeedbackFailedTitle')}
                        description={t('supportErrorDiagnosisFeedbackFailedDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></ErrorFeedbackScreen>
                );
            }
            if (params.get('error')?.toLocaleLowerCase() === EProblemFeedback.ERROR_MASIVE) {
                return (
                    <ErrorFeedbackScreen
                        title={t('supportErrorMasiveFeedbackFailedTitle')}
                        description={t('supportErrorMasiveFeedbackFailedDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></ErrorFeedbackScreen>
                );
            }
            if (params.get('error')?.toLocaleLowerCase() === EProblemFeedback.ERROR_CONNECTION) {
                return (
                    <ErrorFeedbackScreen
                        title={t('supportErrorFeedbackFailedTitle')}
                        description={t('supportErrorFeedbackFailedConnectionDescription')}
                        primaryButton={
                            <ButtonPrimary
                                onPress={() => {
                                    sendToNative(
                                        '/support/incidents',
                                        'modeFlow-backgroundBlue',
                                        t('returnSupportIncidents'),
                                    );
                                }}
                            >
                                {t('supportSuccessFeedbackButton')}
                            </ButtonPrimary>
                        }
                    ></ErrorFeedbackScreen>
                );
            }
        }
        return null;
    };
    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    return (
        <div
            className='incidentsFeedback'
            style={{
                background:
                    params.get('type')?.toLocaleLowerCase() === EProblemFeedbackType.SUCCESS
                        ? skinVars.colors.brand
                        : 'white',
            }}
        >
            {setComponentFeedback()}
        </div>
    );
};

export default IncidentsFeedback;
